import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Download, FileText, Loader2, Receipt, AlertCircle } from 'lucide-react';

export const PortalRechnungen = () => {
  const { user, token } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchInvoices();
  }, [user]);

  const fetchInvoices = async () => {
    if (!user?.id) return;
    
    try {
      const response = await fetch(
        `${backendUrl}/api/portal/invoices?user_id=${user.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setInvoices(data.invoices || []);
      } else {
        console.error('Failed to fetch invoices');
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async (invoice) => {
    if (!invoice.id) {
      toast.error('Keine Rechnungs-ID vorhanden');
      return;
    }
    
    setDownloadingId(invoice.id);
    
    try {
      const response = await fetch(
        `${backendUrl}/api/portal/invoices/${invoice.id}/pdf?user_id=${user.id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Rechnung_${invoice.voucher_number || invoice.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Rechnung heruntergeladen');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Herunterladen');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Fehler beim Herunterladen der Rechnung');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '—';
    return `${Number(amount).toFixed(2)} €`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            Bezahlt
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20">
            Ausstehend
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge className="bg-red-500/10 text-red-600 border-red-500/20">
            Storniert
          </Badge>
        );
      default:
        return (
          <Badge variant="outline">
            {status || 'Unbekannt'}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="space-y-8" data-testid="portal-rechnungen">
        <div>
          <h1 className="font-display text-3xl font-semibold">Rechnungen</h1>
          <p className="mt-2 text-muted-foreground">Alle deine Rechnungen im Überblick</p>
        </div>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Rechnungen</CardTitle>
            <CardDescription>Wird geladen...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-6 w-16" />
                  <Skeleton className="h-8 w-8 ml-auto" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="portal-rechnungen">
      <div>
        <h1 className="font-display text-3xl font-semibold">Rechnungen</h1>
        <p className="mt-2 text-muted-foreground">Alle deine Rechnungen im Überblick</p>
      </div>

      <Card className="glass-card">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Receipt className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Rechnungen</CardTitle>
              <CardDescription>
                {invoices.length > 0 
                  ? `${invoices.length} Rechnung${invoices.length > 1 ? 'en' : ''} gefunden`
                  : 'Herunterladen als PDF'
                }
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Datum</TableHead>
                    <TableHead>Nummer</TableHead>
                    <TableHead>Beschreibung</TableHead>
                    <TableHead className="text-right">Betrag (brutto)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">PDF</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice, index) => (
                    <TableRow 
                      key={invoice.id || index}
                      data-testid={`invoice-row-${invoice.voucher_number || index}`}
                    >
                      <TableCell className="font-medium">
                        {formatDate(invoice.date)}
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-muted px-2 py-0.5 rounded">
                          {invoice.voucher_number || '—'}
                        </code>
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate text-muted-foreground">
                        {invoice.description || 'Wolkenhelden Rechnung'}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatAmount(invoice.amount_gross)}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(invoice.status)}
                      </TableCell>
                      <TableCell className="text-right">
                        {invoice.id ? (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDownloadPDF(invoice)}
                            disabled={downloadingId === invoice.id}
                            data-testid={`download-btn-${invoice.voucher_number || index}`}
                          >
                            {downloadingId === invoice.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="font-medium mb-1">Noch keine Rechnungen</h3>
              <p className="text-muted-foreground text-sm">
                Sobald du eine Bestellung abschließt, erscheinen deine Rechnungen hier.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-muted/30 border-muted">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-muted-foreground mt-0.5" />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">Hinweis zu Rechnungen</p>
              <p>
                Alle Rechnungen werden automatisch bei Bestellabschluss erstellt und per E-Mail versendet. 
                Du kannst sie hier jederzeit erneut herunterladen. Bei Fragen wende dich an{' '}
                <a href="mailto:support@wolkenhelden.de" className="text-primary hover:underline">
                  support@wolkenhelden.de
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
